import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Loader2, UserPlus, LogIn, Users, Chrome } from "lucide-react";
import { LogoMark } from "./Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
    "w-full bg-[#0F172A]/60 border border-slate-700 text-slate-100 px-4 py-3 text-sm placeholder:text-slate-600 focus:outline-none focus:border-sky-400 transition-colors duration-300";

const AuthPage = () => {
    const navigate = useNavigate();
    const [mode, setMode] = useState("register");
    const [form, setForm] = useState({ name: "", email: "", password: "" });
    const [count, setCount] = useState(null);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        axios.get(`${API}/stats`).then((res) => setCount(res.data.users)).catch(() => {});
    }, []);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const googleLogin = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setSending(true);
        try {
            const url = mode === "register" ? `${API}/auth/register` : `${API}/auth/login`;
            const res = await axios.post(url, form);
            localStorage.setItem("site_token", res.data.token);
            localStorage.setItem("site_name", res.data.name);
            navigate("/");
        } catch (err) {
            const detail = err.response?.data?.detail;
            setError(typeof detail === "string" ? detail : "Qualcosa è andato storto. Riprova.");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="relative min-h-screen flex items-center justify-center overflow-hidden px-6">
            <div className="absolute inset-0 graph-paper" aria-hidden="true" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,#020617_80%)]" aria-hidden="true" />

            <div className="relative z-10 w-full max-w-md">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="text-center"
                >
                    <div className="flex justify-center"><LogoMark size={64} /></div>
                    <h1 className="mt-6 font-serif text-4xl text-slate-50 tracking-tight">Ripetizioni²</h1>
                    <p className="mt-3 text-slate-400 text-sm leading-relaxed">
                        Il sito è libero: la registrazione serve solo<br />per lasciare una recensione.
                    </p>
                    <a href="/" data-testid="auth-back-home" className="mt-3 inline-block font-mono text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-sky-400 transition-colors duration-300">
                        ← Torna al sito senza registrarti
                    </a>
                    <div
                        data-testid="auth-counter"
                        className="mt-6 inline-flex items-center gap-2 border border-sky-400/40 bg-sky-400/10 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-sky-300"
                    >
                        <Users size={14} />
                        {count === null ? "…" : count} {count === 1 ? "iscritto" : "iscritti"}
                    </div>
                </motion.div>

                <motion.form
                    data-testid="auth-form"
                    onSubmit={submit}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-10 border border-slate-800 bg-[#0F172A]/40 p-8 space-y-5"
                >
                    <button
                        type="button"
                        data-testid="auth-google-button"
                        onClick={googleLogin}
                        className="w-full inline-flex items-center justify-center gap-3 border border-slate-600 text-slate-100 font-semibold px-8 py-4 hover:border-sky-400 hover:text-sky-400 transition-colors duration-300"
                    >
                        <Chrome size={18} /> Continua con Google
                    </button>
                    <div className="flex items-center gap-4">
                        <span className="flex-1 border-t border-slate-800" />
                        <span className="font-mono text-xs text-slate-600 uppercase tracking-[0.2em]">oppure</span>
                        <span className="flex-1 border-t border-slate-800" />
                    </div>
                    <div className="grid grid-cols-2 border border-slate-700">
                        <button
                            type="button"
                            data-testid="auth-mode-register"
                            onClick={() => { setMode("register"); setError(""); }}
                            className={`py-3 font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${mode === "register" ? "bg-slate-50 text-[#020617]" : "text-slate-400 hover:text-slate-100"}`}
                        >
                            Registrati
                        </button>
                        <button
                            type="button"
                            data-testid="auth-mode-login"
                            onClick={() => { setMode("login"); setError(""); }}
                            className={`py-3 font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${mode === "login" ? "bg-slate-50 text-[#020617]" : "text-slate-400 hover:text-slate-100"}`}
                        >
                            Accedi
                        </button>
                    </div>

                    {mode === "register" && (
                        <input
                            data-testid="auth-input-name"
                            required
                            value={form.name}
                            onChange={set("name")}
                            placeholder="Nome"
                            className={inputCls}
                        />
                    )}
                    <input
                        data-testid="auth-input-email"
                        required
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        placeholder="Email"
                        className={inputCls}
                    />
                    <input
                        data-testid="auth-input-password"
                        required
                        type="password"
                        minLength={6}
                        value={form.password}
                        onChange={set("password")}
                        placeholder="Password (min. 6 caratteri)"
                        className={inputCls}
                    />

                    {error && (
                        <p data-testid="auth-error" className="text-sm text-red-400 border border-red-400/40 bg-red-400/10 px-4 py-3">
                            {error}
                        </p>
                    )}

                    <button
                        data-testid="auth-submit-button"
                        type="submit"
                        disabled={sending}
                        className="w-full inline-flex items-center justify-center gap-3 bg-slate-50 text-[#020617] font-semibold px-8 py-4 hover:bg-sky-400 disabled:opacity-60 transition-colors duration-300"
                    >
                        {sending ? <Loader2 size={18} className="animate-spin" /> : mode === "register" ? <UserPlus size={18} /> : <LogIn size={18} />}
                        {mode === "register" ? "Crea il tuo account" : "Entra"}
                    </button>
                </motion.form>
            </div>
        </div>
    );
};
export default AuthPage;
